// frontend/src/components/PastPosts.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Skeleton,
  Alert,
  Tab,
  Tabs,
  alpha,
  Pagination,
  useTheme,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  CircularProgress,
} from '@mui/material';
import PostStatsDrawer from './PostStatsDrawer';
import SyncIcon from '@mui/icons-material/Sync';
import YouTubeIcon from '@mui/icons-material/YouTube';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CommentIcon from '@mui/icons-material/Comment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ImageIcon from '@mui/icons-material/Image';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { importedPostsApi, socialAccountsApi } from '../services/api';
import { useFeatureToggles } from '../contexts/FeatureToggleContext';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

const PLATFORM_META = {
  instagram: {
    label: 'Instagram',
    Icon: InstagramIcon,
    color: '#E1306C',
    credNote: 'Requires Access Token in Settings.',
    canEdit: false,
    typeFilters: [
      { id: 'ALL', label: 'All' },
      { id: 'IMAGE', label: 'Images' },
      { id: 'VIDEO', label: 'Videos' },
      { id: 'REELS', label: 'Reels' },
      { id: 'CAROUSEL_ALBUM', label: 'Carousel' },
    ],
  },
  facebook: {
    label: 'Facebook',
    Icon: FacebookIcon,
    color: '#1877F2',
    credNote: 'Requires Page Access Token + Page ID in Settings.',
    canEdit: true,
    typeFilters: [
      { id: 'ALL', label: 'All' },
      { id: 'PHOTO', label: 'Photos' },
      { id: 'TEXT', label: 'Text Posts' },
    ],
  },
  youtube: {
    label: 'YouTube',
    Icon: YouTubeIcon,
    color: '#FF0000',
    credNote: 'Requires YouTube OAuth connection in Settings.',
    canEdit: true,
    typeFilters: [
      { id: 'ALL', label: 'All' },
      { id: 'VIDEO', label: 'Videos' },
      { id: 'SHORT', label: 'Shorts' },
    ],
  },
};

// YouTube Shorts heuristic
function isYouTubeShort(post) {
  const text = `${post.title || ''} ${post.content || ''}`.toLowerCase();
  return /#short(s)?\b/.test(text);
}

const SYNC_PLATFORMS = ['instagram', 'facebook', 'youtube'];

function fmt(n) {
  if (n === undefined || n === null) return null;
  return Number(n) >= 1000 ? `${(Number(n) / 1000).toFixed(1)}k` : String(n);
}

// ── Delete Confirmation Dialog ─────────────────────────────────────────────────

function DeleteConfirmDialog({ open, post, onClose, onConfirm, loading }) {
  const meta = post ? PLATFORM_META[post.platform] : null;
  const color = meta?.color || '#6366f1';
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <DeleteIcon sx={{ color: '#ef4444' }} />
        Delete Post
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          This will permanently delete the post from{' '}
          <Typography component="span" fontWeight={700} sx={{ color }}>
            {meta?.label}
          </Typography>{' '}
          and remove it from your library. This cannot be undone.
        </Typography>
        {post?.title && (
          <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: '8px' }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
              Post
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
            }}>
              {post.title}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} sx={{ borderRadius: '4px' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
          sx={{ borderRadius: '4px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
        >
          {loading ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Edit Post Drawer ───────────────────────────────────────────────────────────

function EditPostDrawer({ open, post, onClose, onSave, loading, error }) {
  const theme = useTheme();
  const meta = post ? PLATFORM_META[post.platform] : null;
  const color = meta?.color || '#6366f1';

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [privacy, setPrivacy] = useState('public');

  // Sync form state when the post changes
  useEffect(() => {
    if (post) {
      setTitle(post.title || '');
      setDescription(post.content || '');
      setContent(post.content || '');
      setPrivacy('public');
    }
  }, [post]);

  const handleSave = () => {
    if (post?.platform === 'youtube') {
      onSave({ title, description, privacy });
    } else if (post?.platform === 'facebook') {
      onSave({ content });
    }
  };

  const isYT = post?.platform === 'youtube';
  const isFB = post?.platform === 'facebook';
  const isIG = post?.platform === 'instagram';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          p: 3,
          background: theme.palette.background.paper,
        }
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon sx={{ color }} />
          <Typography variant="h6" fontWeight={700}>
            Edit Post
          </Typography>
          {meta && (
            <Chip
              icon={<meta.Icon sx={{ fontSize: 13 }} />}
              label={meta.label}
              size="small"
              sx={{ bgcolor: alpha(color, 0.12), color, fontWeight: 700, '& .MuiChip-icon': { color } }}
            />
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: '8px' }}>
          {error}
        </Alert>
      )}

      {isIG && (
        <Alert
          icon={<InfoOutlinedIcon />}
          severity="info"
          sx={{ mb: 3, borderRadius: '8px' }}
        >
          Instagram does not allow editing published posts via the API. You can only delete this post.
        </Alert>
      )}

      {isYT && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label="Title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            fullWidth
            size="small"
            inputProps={{ maxLength: 100 }}
            helperText={`${title.length}/100`}
          />
          <TextField
            label="Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={4}
            maxRows={10}
            size="small"
            inputProps={{ maxLength: 5000 }}
            helperText={`${description.length}/5000`}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Privacy</InputLabel>
            <Select value={privacy} label="Privacy" onChange={e => setPrivacy(e.target.value)}>
              <MenuItem value="public">Public</MenuItem>
              <MenuItem value="unlisted">Unlisted</MenuItem>
              <MenuItem value="private">Private</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {isFB && (
        <TextField
          label="Post Message"
          value={content}
          onChange={e => setContent(e.target.value)}
          fullWidth
          multiline
          minRows={5}
          maxRows={14}
          size="small"
          inputProps={{ maxLength: 63206 }}
          helperText={`${content.length} characters`}
        />
      )}

      {/* Footer actions */}
      {!isIG && (
        <Box sx={{ mt: 'auto', pt: 4, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button onClick={onClose} disabled={loading} sx={{ borderRadius: '4px' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <EditIcon />}
            sx={{ borderRadius: '4px', background: `linear-gradient(45deg, ${color}, ${alpha(color, 0.7)})` }}
          >
            {loading ? 'Saving…' : 'Save Changes'}
          </Button>
        </Box>
      )}
    </Drawer>
  );
}

// ── Post Card ──────────────────────────────────────────────────────────────────

function PostCard({ post, onClick, onEdit, onDelete }) {
  const theme = useTheme();
  const meta = PLATFORM_META[post.platform] || {};
  const { Icon, color = '#6366f1', canEdit } = meta;
  const stats = post.stats || {};
  const isVideo = post.media_type === 'VIDEO' || post.media_type === 'REELS' || post.media_type === 'SHORT';
  const [imgFail, setImgFail] = useState(false);
  const [hovered, setHovered] = useState(false);

  const metrics = [
    { icon: <VisibilityIcon sx={{ fontSize: 12 }} />, val: stats.views,    show: !!stats.views },
    { icon: <ThumbUpIcon    sx={{ fontSize: 12 }} />, val: stats.likes,    show: !!stats.likes },
    { icon: <CommentIcon    sx={{ fontSize: 12 }} />, val: stats.comments, show: !!stats.comments },
  ].filter(m => m.show);

  return (
    <Box
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        cursor: 'pointer',
        borderRadius: '16px',
        border: `1.5px solid ${alpha(color, 0.25)}`,
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        background: theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.paper, 0.7)
          : '#fff',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: `0 12px 32px ${alpha(color, 0.2)}`,
          borderColor: alpha(color, 0.5),
        },
      }}
    >
      {/* 16:9 Thumbnail */}
      <Box sx={{ position: 'relative', aspectRatio: '16/9', bgcolor: alpha(color, 0.08), overflow: 'hidden' }}>
        {post.media_url && !imgFail ? (
          <img
            src={post.media_url}
            alt={post.title || ''}
            onError={() => setImgFail(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {isVideo
              ? <PlayCircleIcon sx={{ fontSize: 40, color: alpha(color, 0.3) }} />
              : <ImageIcon       sx={{ fontSize: 40, color: alpha(color, 0.3) }} />}
          </Box>
        )}

        {/* Hover action buttons — top right */}
        <Box
          sx={{
            position: 'absolute', top: 6, right: 6,
            display: 'flex', gap: 0.5,
            opacity: hovered ? 1 : 0,
            transition: 'opacity 0.18s ease',
          }}
        >
          {canEdit && (
            <IconButton
              size="small"
              aria-label="Edit"
              onClick={e => { e.stopPropagation(); onEdit(post); }}
              sx={{
                bgcolor: alpha('#000', 0.55), color: '#fff', backdropFilter: 'blur(4px)',
                width: 28, height: 28,
                '&:hover': { bgcolor: alpha(color, 0.85) },
              }}
            >
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
          <IconButton
            size="small"
            aria-label="Delete"
            onClick={e => { e.stopPropagation(); onDelete(post); }}
            sx={{
              bgcolor: alpha('#000', 0.55), color: '#fff', backdropFilter: 'blur(4px)',
              width: 28, height: 28,
              '&:hover': { bgcolor: alpha('#ef4444', 0.85) },
            }}
          >
            <DeleteIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        {/* Media type chip — bottom right */}
        {post.media_type && (
          <Chip
            icon={isVideo ? <PlayCircleIcon sx={{ fontSize: 11 }} /> : <ImageIcon sx={{ fontSize: 11 }} />}
            label={post.media_type}
            size="small"
            sx={{
              position: 'absolute', bottom: 8, right: 8,
              height: 20, fontSize: '0.58rem', fontWeight: 800,
              bgcolor: alpha('#000', 0.55), color: '#fff', backdropFilter: 'blur(4px)',
              '& .MuiChip-icon': { color: '#fff !important', ml: '4px' },
            }}
          />
        )}
      </Box>

      {/* Info section */}
      <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Platform row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
          {Icon && <Icon sx={{ fontSize: 15, color }} />}
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {meta.label || post.platform}
          </Typography>
          {post.account_display_name && (
            <Chip
              label={post.account_display_name}
              size="small"
              sx={{
                height: 16, fontSize: '0.58rem', fontWeight: 700,
                bgcolor: alpha(color, 0.1), color,
                border: `1px solid ${alpha(color, 0.2)}`,
                '& .MuiChip-label': { px: '5px' },
              }}
            />
          )}
          {post.published_at && (
            <Typography sx={{ fontSize: '0.58rem', color: 'text.disabled', ml: 'auto' }}>
              {new Date(post.published_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
            </Typography>
          )}
        </Box>

        {/* Title / caption — grows to push metrics to bottom */}
        <Typography sx={{
          fontSize: '0.82rem', lineHeight: 1.4, fontWeight: 600,
          color: post.title ? 'text.primary' : 'text.disabled',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          mb: metrics.length ? 1.25 : 0,
          flex: 1,
        }}>
          {post.title || '(no caption)'}
        </Typography>

        {/* Metrics row */}
        {metrics.length > 0 && (
          <Box sx={{ display: 'flex', gap: 2, pt: 1, borderTop: `1px solid ${alpha(theme.palette.divider, 0.07)}` }}>
            {metrics.map((m, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: 'text.secondary' }}>
                {m.icon}
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color }}>
                  {fmt(m.val)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* ROI row — only shown once impact data exists on the post */}
        {(post.roi_score != null || post.follower_delta_72h != null) && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pt: 1, mt: metrics.length ? 0 : 1, borderTop: metrics.length ? 'none' : `1px solid ${alpha(theme.palette.divider, 0.07)}` }}>
            {post.roi_score != null && (
              <Chip
                label={`ROI ${Number(post.roi_score).toFixed(1)}`}
                size="small"
                sx={{
                  height: 18, fontSize: '0.62rem', fontWeight: 800,
                  bgcolor: alpha('#f59e0b', 0.12), color: '#b45309',
                  border: `1px solid ${alpha('#f59e0b', 0.25)}`,
                  '& .MuiChip-label': { px: '6px' },
                }}
              />
            )}
            {post.follower_delta_72h != null && (
              <Chip
                label={`${post.follower_delta_72h >= 0 ? '+' : ''}${post.follower_delta_72h} 👥`}
                size="small"
                sx={{
                  height: 18, fontSize: '0.62rem', fontWeight: 800,
                  bgcolor: alpha(post.follower_delta_72h >= 0 ? '#10b981' : '#ef4444', 0.1),
                  color: post.follower_delta_72h >= 0 ? '#065f46' : '#991b1b',
                  border: `1px solid ${alpha(post.follower_delta_72h >= 0 ? '#10b981' : '#ef4444', 0.25)}`,
                  '& .MuiChip-label': { px: '6px' },
                }}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function SkeletonGrid() {
  return (
    <Grid container spacing={2.5}>
      {Array.from({ length: 12 }).map((_, i) => (
        <Grid key={i} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <Box sx={{ borderRadius: '16px', overflow: 'hidden', border: '1.5px solid rgba(99,102,241,0.12)' }}>
            <Skeleton variant="rectangular" sx={{ aspectRatio: '16/9', width: '100%' }} />
            <Box sx={{ p: 2 }}>
              <Skeleton sx={{ height: 14, mb: 0.75, width: '40%' }} />
              <Skeleton sx={{ height: 16 }} />
              <Skeleton sx={{ height: 16, width: '70%', mb: 1 }} />
              <Skeleton sx={{ height: 12, width: '60%' }} />
            </Box>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
}

// 4 cols × 3 rows = 12 — always fills the grid perfectly with no orphan cells
const PER_PAGE = 12;

const PastPosts = () => {
  const [tab, setTab] = useState(0);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);
  const [error, setError] = useState('');
  const [syncMessage, setSyncMessage] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [page, setPage] = useState(1);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Edit state
  const [editTarget, setEditTarget] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  const { getAllowedPlatforms } = useFeatureToggles();
  const enabledPlatforms = SYNC_PLATFORMS.filter(p => getAllowedPlatforms('posting').includes(p));
  const activePlatform = enabledPlatforms[tab] || null;

  const fetchPosts = useCallback(async (platform) => {
    setLoading(true);
    setError('');
    try {
      const data = await importedPostsApi.getAll(platform, 1, 500);
      if (data && data.posts) {
        setAllPosts(data.posts);
      } else {
        setAllPosts(data || []);
      }
    } catch (err) {
      setError(err.message || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activePlatform) fetchPosts(activePlatform);
  }, [activePlatform, fetchPosts]);

  // ── Auto-refresh: postPublished event + page visibility + 60s interval ──────
  useEffect(() => {
    if (!activePlatform) return;

    // When a post is published from the Post page, do a quick re-fetch after a
    // short delay (gives the backend time to write to Firestore first).
    const onPostPublished = (e) => {
      const platforms = e?.detail?.platforms || [];
      if (platforms.length === 0 || platforms.includes(activePlatform)) {
        setTimeout(() => fetchPosts(activePlatform), 1500);
      }
    };

    // When the user returns to this browser tab, refresh silently.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchPosts(activePlatform);
      }
    };

    // Periodic background refresh every 60 seconds.
    const interval = setInterval(() => fetchPosts(activePlatform), 60_000);

    window.addEventListener('postPublished', onPostPublished);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('postPublished', onPostPublished);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, [activePlatform, fetchPosts]);

  useEffect(() => {
    socialAccountsApi.list()
      .then(data => setSocialAccounts(data.accounts || []))
      .catch(() => {});
  }, []);

  // Reset selected account when platform tab changes
  useEffect(() => {
    setSelectedAccountId(null);
  }, [tab]);

  const handleSync = async () => {
    if (!activePlatform) return;
    setSyncing(activePlatform);
    setSyncMessage('');
    setError('');
    try {
      const result = await importedPostsApi.sync(activePlatform, selectedAccountId);
      const acctName = selectedAccountId
        ? socialAccounts.find(a => a.account_id === selectedAccountId)?.display_name || ''
        : '';
      const fromLabel = acctName ? ` from @${acctName}` : ` from ${PLATFORM_META[activePlatform]?.label}`;
      setSyncMessage(`Synced ${result.synced} posts${fromLabel}.`);
      await fetchPosts(activePlatform);
    } catch (err) {
      setError(err.message || `Failed to sync ${activePlatform}`);
    } finally {
      setSyncing(null);
    }
  };

  const handleTabChange = (_, newVal) => {
    setTab(newVal);
    setSyncMessage('');
    setError('');
    setTypeFilter('ALL');
    setPage(1);
  };

  const handleTypeFilter = (id) => {
    setTypeFilter(id);
    setPage(1);
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Delete handlers ──────────────────────────────────────────────────────────

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await importedPostsApi.deletePost(deleteTarget.id);
      setAllPosts(prev => prev.filter(p => p.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setError(err.message || 'Failed to delete post');
      setDeleteTarget(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Edit handlers ────────────────────────────────────────────────────────────

  const handleEditSave = async (updates) => {
    if (!editTarget) return;
    setEditLoading(true);
    setEditError('');
    try {
      await importedPostsApi.editPost(editTarget.id, updates);
      // Optimistically update local post title/content
      setAllPosts(prev => prev.map(p => {
        if (p.id !== editTarget.id) return p;
        return {
          ...p,
          title: updates.title ?? p.title,
          content: updates.description ?? updates.content ?? p.content,
        };
      }));
      setEditTarget(null);
    } catch (err) {
      // Show error inside the drawer so user can retry without losing the form
      setEditError(err.message || 'Failed to update post');
    } finally {
      setEditLoading(false);
    }
  };

  // Client-side filter
  const filteredPosts = allPosts.filter((post) => {
    if (typeFilter === 'ALL') return true;
    if (activePlatform === 'youtube') {
      if (typeFilter === 'SHORT') return isYouTubeShort(post);
      if (typeFilter === 'VIDEO') return !isYouTubeShort(post);
    }
    return post.media_type === typeFilter;
  });

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / PER_PAGE));
  const pagedPosts = filteredPosts.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const platformMeta = activePlatform ? PLATFORM_META[activePlatform] : null;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', mb: 1 }}>
            Past Posts
          </Typography>
          <Typography variant="body1" color="text.secondary" fontWeight={500}>
            Sync and browse your published content from connected platforms.
          </Typography>
        </Box>

        {activePlatform && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            {/* Account selector — shown when >1 account connected for this platform */}
            {socialAccounts.filter(a => a.platform === activePlatform).length > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>Sync account:</Typography>
                <Chip
                  icon={<AccountCircleIcon sx={{ fontSize: '14px !important' }} />}
                  label="All"
                  size="small"
                  onClick={() => setSelectedAccountId(null)}
                  variant={selectedAccountId === null ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 700, ...(selectedAccountId === null && { bgcolor: platformMeta?.color, color: '#fff' }) }}
                />
                {socialAccounts.filter(a => a.platform === activePlatform).map(acct => (
                  <Chip
                    key={acct.account_id}
                    icon={<AccountCircleIcon sx={{ fontSize: '14px !important' }} />}
                    label={acct.display_name || acct.ig_username || acct.page_name || acct.channel_name || acct.account_id}
                    size="small"
                    onClick={() => setSelectedAccountId(acct.account_id)}
                    variant={selectedAccountId === acct.account_id ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 700, ...(selectedAccountId === acct.account_id && { bgcolor: platformMeta?.color, color: '#fff' }) }}
                  />
                ))}
              </Box>
            )}
            <Button
              variant="contained"
              startIcon={<SyncIcon sx={{ animation: syncing === activePlatform ? 'spin 1s linear infinite' : 'none' }} />}
              onClick={handleSync}
              disabled={!!syncing}
              sx={{
                borderRadius: 3,
                px: 3,
                fontWeight: 800,
                bgcolor: platformMeta?.color,
                '&:hover': { bgcolor: platformMeta?.color, opacity: 0.9 },
                '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
              }}
            >
              {syncing === activePlatform ? 'Syncing…' : `Sync ${platformMeta?.label}`}
            </Button>
          </Box>
        )}
      </Box>

      {/* Alerts */}
      {syncMessage && (
        <Alert severity="success" onClose={() => setSyncMessage('')} sx={{ mb: 3, borderRadius: 3 }}>
          {syncMessage}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 3, borderRadius: 3 }}>
          {error}
        </Alert>
      )}

      {/* Platform tabs */}
      {enabledPlatforms.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          No platforms are enabled for posting. Enable platforms in Settings to sync posts.
        </Alert>
      ) : (
        <>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            {enabledPlatforms.map((p) => {
              const { label, Icon, color } = PLATFORM_META[p];
              return (
                <Tab
                  key={p}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Icon sx={{ fontSize: 18, color: tab === enabledPlatforms.indexOf(p) ? color : 'inherit' }} />
                      {label}
                    </Box>
                  }
                  sx={{ fontWeight: 700, textTransform: 'none' }}
                />
              );
            })}
          </Tabs>

          {/* Content type filter chips */}
          {!loading && allPosts.length > 0 && platformMeta?.typeFilters && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {platformMeta.typeFilters.map((f) => {
                const count = f.id === 'ALL' ? allPosts.length : allPosts.filter((p) => {
                  if (activePlatform === 'youtube') {
                    if (f.id === 'SHORT') return isYouTubeShort(p);
                    if (f.id === 'VIDEO') return !isYouTubeShort(p);
                  }
                  return p.media_type === f.id;
                }).length;
                if (f.id !== 'ALL' && count === 0) return null;
                return (
                  <Chip
                    key={f.id}
                    label={`${f.label} (${count})`}
                    onClick={() => handleTypeFilter(f.id)}
                    variant={typeFilter === f.id ? 'filled' : 'outlined'}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      ...(typeFilter === f.id && {
                        bgcolor: platformMeta.color,
                        color: '#fff',
                        '&:hover': { bgcolor: platformMeta.color, opacity: 0.9 },
                      }),
                    }}
                  />
                );
              })}
            </Box>
          )}

          {/* Content */}
          {loading ? (
            <SkeletonGrid />
          ) : allPosts.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 10,
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 4,
              }}
            >
              {platformMeta?.Icon && (
                <platformMeta.Icon sx={{ fontSize: 56, color: alpha(platformMeta.color, 0.25), mb: 2 }} />
              )}
              <Typography variant="h6" fontWeight={700} gutterBottom>
                No posts synced yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {platformMeta?.credNote} Then click "Sync {platformMeta?.label}" to import your posts.
              </Typography>
              <Button variant="outlined" startIcon={<SyncIcon />} onClick={handleSync} disabled={!!syncing} sx={{ borderRadius: 3 }}>
                {syncing ? 'Syncing…' : `Sync ${platformMeta?.label} now`}
              </Button>
            </Box>
          ) : filteredPosts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="body1" color="text.secondary">
                No {platformMeta?.typeFilters?.find(f => f.id === typeFilter)?.label?.toLowerCase()} found.
              </Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={2.5}>
                {pagedPosts.map((post) => (
                  <Grid key={post.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <PostCard
                      post={post}
                      onClick={() => setSelectedPostId(post.id)}
                      onEdit={setEditTarget}
                      onDelete={setDeleteTarget}
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4, gap: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {filteredPosts.length} {typeFilter !== 'ALL' ? `${platformMeta?.typeFilters?.find(f => f.id === typeFilter)?.label?.toLowerCase() || ''} ` : ''}posts
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    shape="rounded"
                    size="medium"
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* Per-post detail drawer */}
      <PostStatsDrawer
        postId={selectedPostId}
        open={!!selectedPostId}
        onClose={() => setSelectedPostId(null)}
      />

      {/* Delete confirmation dialog */}
      <DeleteConfirmDialog
        open={!!deleteTarget}
        post={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
      />

      {/* Edit drawer */}
      <EditPostDrawer
        open={!!editTarget}
        post={editTarget}
        onClose={() => { setEditTarget(null); setEditError(''); }}
        onSave={handleEditSave}
        loading={editLoading}
        error={editError}
      />
    </Box>
  );
};

export default PastPosts;
